/**
 * Ticketing Setup. The event is the header's selection.
 *
 *  - Overview (/ticketing-setup): Event Setup on top (storefront settings and
 *    the tab list, both event-level), then the ticket list. The list has a
 *    search box, an "Active only" filter, sortable headers and an inline
 *    Active switch. Each ticket has a "Set up" button; "New ticket" starts
 *    the flow for a new one.
 *  - Ticket flow (/ticketing-setup/ticket/:ticketId/:step): one ticket type
 *    at a time through Details, Modifiers, Placement, Gates, Review. Every
 *    step reopens from the bar.
 *
 * It reads and writes the same tables as the older pages, so both can coexist.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, LoaderFunctionArgs, useLoaderData, useLocation, useNavigate, useParams, useRevalidator } from "react-router-dom";
import toast from "react-hot-toast";
import { ChevronDownIcon, ChevronRightIcon, ChevronUpDownIcon, ChevronUpIcon } from "@heroicons/react/20/solid";
import { MagnifyingGlassIcon, PlusIcon } from "@heroicons/react/24/solid";
import { createApolloClient } from "../../../providers/ApolloClientFactory";
import { organizationStore } from "../../../store/organization";
import { eventStore, useEventStore } from "../../../store/event";
import { userStore } from "../../../store/user";
import { GET_LIST_EVENTS_BY_ORGANIZATION, GET_LIST_VENDORS_BY_ORGANIZATION } from "../../../queries";
import { PrimaryButton, SecondaryButton } from "../../../components/Button";
import { TicketingItem, TicketingTab } from "../../../types/tickets";
import { GET_ITEM_CATEGORIES, GET_TICKETING_TABS } from "../ticket-items/queries";
import { parseTicketModifiers } from "../../tokens/variantName";
import { GET_SETUP_EVENT, GET_SETUP_GATES, GET_SETUP_PROCESSORS, GET_SETUP_TICKETS, UPDATE_ITEMS_TICKETING_MANY } from "./queries";
import { SetupData, SetupEvent, SetupGate, SetupProcessor, SetupVendor, StepKey, isStepKey, ACCESS_CONTROL_TYPE, PUBLIC_SITE_BASE, EVENT_SETUP_ANCHOR, OVERVIEW_PATH, ticketPath } from "./types";
import { SetupCheck, computeEventChecks, computeTicketChecks } from "./checks";
import { Card, CheckRow, Chip, Stepper, money } from "./ui";
import { StorefrontStep } from "./steps/StorefrontStep";
import { TabsManager } from "./steps/TabsManager";
import { GatesManager } from "./steps/GatesManager";
import { DetailsStep } from "./steps/DetailsStep";
import { ModifiersStep } from "./steps/ModifiersStep";
import { PlacementStep } from "./steps/PlacementStep";
import { GatesStep } from "./steps/GatesStep";
import { ReviewStep } from "./steps/ReviewStep";
import { describeGrants } from "./tokenPayload";

const client = createApolloClient();
const { getState: getOrganizationState } = organizationStore;
const { getState: getEventState } = eventStore;
const { getState: getUserState } = userStore;

// The event comes from the dashboard header's event dropdown (eventStore).
export const ticketingSetupLoader = async (_args: LoaderFunctionArgs): Promise<SetupData> => {
  const rawOrganizationId = getOrganizationState().organizationId;
  const organizationId = parseInt(rawOrganizationId !== null ? String(rawOrganizationId) : "", 10) || null;
  const user = getUserState().user;
  const rawEventId = getEventState().eventId;
  const eventId = rawEventId ? Number(rawEventId) : null;

  const eventsWhere: any = {};
  if (organizationId) {
    eventsWhere.organization_id = { _eq: organizationId };
    eventsWhere.is_active = { _eq: true };
  }
  if (user?.events?.length) eventsWhere.id = { _in: user.events };

  const { data: eventsData } = await client.query({
    query: GET_LIST_EVENTS_BY_ORGANIZATION,
    variables: { where: eventsWhere },
  });
  const events = (eventsData?.events ?? []) as { id: number; name: string }[];

  const base: SetupData = { events, eventId, event: null, processors: [], tickets: [], tabs: [], gates: [], vendors: [], categories: [], organizationId };
  if (!eventId) return base;

  // Same vendor scope as the Location create page: the organization, narrowed to the user's vendors.
  const vendorsWhere: any = {};
  if (organizationId) vendorsWhere.organization_id = { _eq: organizationId };
  if (user?.vendors?.length) vendorsWhere.id = { _in: user.vendors };

  const net = { fetchPolicy: "network-only" as const };
  const [{ data: ev }, { data: procs }, { data: tix }, { data: tabs }, { data: gates }, { data: vendors }, { data: cats }] = await Promise.all([
    client.query({ query: GET_SETUP_EVENT, variables: { id: eventId }, ...net }),
    client.query({ query: GET_SETUP_PROCESSORS, variables: { where: organizationId ? { organization_id: { _eq: organizationId } } : {} }, ...net }),
    client.query({ query: GET_SETUP_TICKETS, variables: { eventId }, ...net }),
    client.query({ query: GET_TICKETING_TABS, variables: { where: { event_id: { _eq: eventId } } }, ...net }),
    client.query({ query: GET_SETUP_GATES, variables: { eventId }, ...net }),
    client.query({ query: GET_LIST_VENDORS_BY_ORGANIZATION, variables: { where: vendorsWhere } }),
    client.query({
      query: GET_ITEM_CATEGORIES,
      variables: {
        where: organizationId
          ? { organization_id: { _in: [0, organizationId] }, is_active: { _eq: true }, reference_type: { _eq: "item_ticketing_category" } }
          : { reference_type: { _eq: "item_ticketing_category" } },
      },
    }),
  ]);

  return {
    ...base,
    event: (ev?.events_by_pk ?? null) as SetupEvent | null,
    processors: (procs?.payment_processor_config ?? []) as SetupProcessor[],
    tickets: (tix?.items_ticketing ?? []) as TicketingItem[],
    tabs: (tabs?.items_ticketing_tabs ?? []) as TicketingTab[],
    gates: (gates?.locations ?? []) as SetupGate[],
    vendors: (vendors?.vendors ?? []) as SetupVendor[],
    categories: (cats?.dashboard_reference ?? []) as { reference_label: string; reference_value: string }[],
  };
};

/** Re-run the loader when the header's event changes (the header only revalidates "list"/"reports" routes). */
const useFollowHeaderEvent = (data: SetupData) => {
  const revalidator = useRevalidator();
  const headerEventId = useEventStore((state) => state.eventId);
  useEffect(() => {
    const selected = headerEventId ? Number(headerEventId) : null;
    if (selected !== data.eventId && revalidator.state === "idle") revalidator.revalidate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerEventId]);
  return revalidator;
};

const PageHeader = ({ data, title, sub }: { data: SetupData; title: string; sub: React.ReactNode }) => {
  const slug = data.event?.ticketing_slug?.trim();
  const publicUrl = slug ? PUBLIC_SITE_BASE + slug : null;
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">{title}{data.event ? ` · ${data.event.name}` : ""}</h2>
        <p className="text-sm text-gray-600">{sub}</p>
      </div>
      {publicUrl && (
        <a href={publicUrl} target="_blank" rel="noopener noreferrer"><SecondaryButton>Public page</SecondaryButton></a>
      )}
    </div>
  );
};

const NoEvent = () => (
  <div className="bg-white rounded-md shadow-sm p-8 text-center text-sm text-gray-500">Select an event in the header to start.</div>
);

// ---------------------------------------------------------------- overview

const YesNo = ({ value }: { value: boolean }) =>
  value ? <span className="text-green-700 font-medium">Yes</span> : <span className="text-gray-400">No</span>;

/** Inline on/off switch for a ticket flag. */
const Toggle = ({ on, busy, label, onChange }: { on: boolean; busy: boolean; label: string; onChange: () => void }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={label}
    title={label}
    disabled={busy}
    onClick={onChange}
    className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-1 disabled:cursor-wait disabled:opacity-60 ${on ? "bg-green-600" : "bg-gray-300"}`}
  >
    <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${on ? "translate-x-4" : "translate-x-0.5"}`} />
  </button>
);

type TicketSortKey = "name" | "price" | "tab" | "grants" | "active" | "public" | "setup";
type SortDir = "asc" | "desc";
type TicketSort = { key: TicketSortKey; dir: SortDir } | null;

/** One ticket with the derived values the table shows, searches and sorts on. */
interface TicketRow {
  t: TicketingItem;
  name: string;
  tab: string;
  grants: string;
  /** is_active, with an in-flight toggle applied until the loader catches up. */
  active: boolean;
  mods: number;
  errors: number;
  warnings: number;
  /** 0 ready, 1 warnings, 2 problems. */
  setupRank: number;
}

/** The first click on a header: text and price ascend; flags and setup status put Yes / problems first. */
const FIRST_DIR: Record<TicketSortKey, SortDir> = { name: "asc", price: "asc", tab: "asc", grants: "asc", active: "desc", public: "desc", setup: "desc" };

const compareRows = (a: TicketRow, b: TicketRow, key: TicketSortKey): number => {
  switch (key) {
    case "name": return a.name.localeCompare(b.name);
    case "price": return (Number(a.t.price) || 0) - (Number(b.t.price) || 0);
    case "tab": return a.tab.localeCompare(b.tab);
    case "grants": return a.grants.localeCompare(b.grants);
    case "active": return Number(a.active) - Number(b.active);
    case "public": return Number(!!a.t.available_public_sale) - Number(!!b.t.available_public_sale);
    case "setup": return a.setupRank - b.setupRank || a.errors - b.errors || a.warnings - b.warnings;
  }
};

const ALIGN = { left: "text-left justify-start", right: "text-right justify-end", center: "text-center justify-center" } as const;

const SortHeader = ({ label, sortKey, sort, onSort, align = "left" }: { label: string; sortKey: TicketSortKey; sort: TicketSort; onSort: (key: TicketSortKey) => void; align?: keyof typeof ALIGN }) => {
  const isSorted = sort?.key === sortKey;
  const Icon = !isSorted ? ChevronUpDownIcon : sort!.dir === "asc" ? ChevronUpIcon : ChevronDownIcon;
  return (
    <th className="py-2 pr-3" aria-sort={isSorted ? (sort!.dir === "asc" ? "ascending" : "descending") : "none"}>
      <button type="button" onClick={() => onSort(sortKey)} className={`inline-flex w-full items-center gap-1 uppercase hover:text-gray-800 ${ALIGN[align]} ${isSorted ? "text-gray-900" : ""}`}>
        {label}
        <Icon className={`h-4 w-4 shrink-0 ${isSorted ? "text-gray-700" : "text-gray-400"}`} />
      </button>
    </th>
  );
};

export const TicketingSetupOverview = () => {
  const data = useLoaderData() as SetupData;
  const revalidator = useFollowHeaderEvent(data);
  const navigate = useNavigate();
  const refresh = () => revalidator.revalidate();
  const eventChecks = useMemo(() => computeEventChecks(data), [data]);

  // Ticket table controls. No sort keeps the loader's order (sort_order, then name).
  const [query, setQuery] = useState("");
  const [activeOnly, setActiveOnly] = useState(false);
  const [sort, setSort] = useState<TicketSort>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  // is_active values written but not yet reloaded, so the switch never snaps back while the loader runs.
  const [pendingActive, setPendingActive] = useState<Record<number, boolean>>({});
  useEffect(() => {
    setPendingActive((p) => {
      const next = { ...p };
      for (const t of data.tickets) if (next[t.id] === !!t.is_active) delete next[t.id];
      return Object.keys(next).length === Object.keys(p).length ? p : next;
    });
  }, [data.tickets]);

  const allRows = useMemo<TicketRow[]>(
    () =>
      data.tickets.map((t) => {
        const checks = computeTicketChecks(data, t);
        const errors = checks.filter((c) => c.level === "error").length;
        const warnings = checks.filter((c) => c.level === "warning").length;
        return {
          t,
          name: t.name ?? "",
          tab: t.tab?.name ?? "",
          grants: describeGrants(t),
          active: pendingActive[t.id] ?? !!t.is_active,
          mods: parseTicketModifiers(t.modifiers).length,
          errors,
          warnings,
          setupRank: errors ? 2 : warnings ? 1 : 0,
        };
      }),
    [data, pendingActive]
  );

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = allRows.filter(
      (r) => (!activeOnly || r.active) && (!q || [r.name, r.tab, r.grants, r.t.category ?? ""].some((s) => s.toLowerCase().includes(q)))
    );
    if (!sort) return list;
    const sign = sort.dir === "asc" ? 1 : -1;
    return [...list].sort((a, b) => sign * compareRows(a, b, sort.key) || a.name.localeCompare(b.name));
  }, [allRows, query, activeOnly, sort]);

  // Click cycle per header: first direction, the other direction, then back to the default order.
  const onSort = (key: TicketSortKey) =>
    setSort((s) => (s?.key !== key ? { key, dir: FIRST_DIR[key] } : s.dir === FIRST_DIR[key] ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : null));

  // Same many-update mutation the Placement step uses; the loader reloads the list afterwards.
  const toggleActive = async (row: TicketRow) => {
    const nextActive = !row.active;
    setTogglingId(row.t.id);
    try {
      await client.mutate({
        mutation: UPDATE_ITEMS_TICKETING_MANY,
        variables: { updates: [{ where: { id: { _eq: row.t.id } }, _set: { is_active: nextActive, last_updated_by: getUserState().user?.id } }] },
      });
      await client.clearStore();
      setPendingActive((p) => ({ ...p, [row.t.id]: nextActive }));
      toast.success(`${row.name} is now ${nextActive ? "active" : "inactive"}`);
      refresh();
    } catch (err) {
      toast.error("Failed to update the ticket: " + err);
    } finally {
      setTogglingId(null);
    }
  };

  const count = rows.length === allRows.length ? `${allRows.length} ticket${allRows.length === 1 ? "" : "s"}` : `${rows.length} of ${allRows.length} tickets`;

  return (
    <div className="mt-3 flex flex-col gap-4" aria-busy={revalidator.state === "loading"}>
      <PageHeader data={data} title="Ticketing Setup" sub="Event Setup applies to every ticket. Set up each ticket type on its own, one at a time." />
      {!data.eventId || !data.event ? (
        <NoEvent />
      ) : (
        <>
          <EventSetupSection data={data} refresh={refresh} checks={eventChecks} />
          <Card title="Tickets" subtitle="One row per ticket type. Set up walks through details, modifiers, placement and gates for that ticket only.">
            {data.tickets.length === 0 ? (
              <div className="text-sm text-gray-500">No tickets yet.</div>
            ) : (
              <>
                <div className="flex flex-wrap items-center gap-3">
                  <label className="relative block w-full sm:w-80">
                    <span className="sr-only">Search tickets</span>
                    <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <input
                      type="search"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Search by name, tab, grants or category"
                      className="block w-full rounded-md border-gray-300 py-1.5 pl-8 pr-3 text-sm focus:border-primary-600 focus:ring-primary-600"
                    />
                  </label>
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={activeOnly}
                      onChange={(e) => setActiveOnly(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                    />
                    Active only
                  </label>
                  <span className="ml-auto text-xs text-gray-500">
                    {count}
                    {sort && (
                      <>
                        {" · "}
                        <button type="button" className="text-primary-700 hover:text-primary-600" onClick={() => setSort(null)}>Clear sort</button>
                      </>
                    )}
                  </span>
                </div>
                {rows.length === 0 ? (
                  <div className="text-sm text-gray-500">No tickets match.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs font-semibold text-gray-500 uppercase">
                          <SortHeader label="Ticket" sortKey="name" sort={sort} onSort={onSort} />
                          <SortHeader label="Price" sortKey="price" sort={sort} onSort={onSort} align="right" />
                          <SortHeader label="Tab" sortKey="tab" sort={sort} onSort={onSort} />
                          <SortHeader label="Grants" sortKey="grants" sort={sort} onSort={onSort} />
                          <SortHeader label="Active" sortKey="active" sort={sort} onSort={onSort} align="center" />
                          <SortHeader label="Public sale" sortKey="public" sort={sort} onSort={onSort} align="center" />
                          <SortHeader label="Setup" sortKey="setup" sort={sort} onSort={onSort} />
                          <th className="py-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row) => {
                          const { t, mods, errors, warnings, grants, active } = row;
                          return (
                            <tr key={t.id} className="border-t border-gray-100">
                              <td className={`py-2 pr-3 font-medium ${active ? "text-gray-900" : "text-gray-500"}`}>
                                {t.name}
                                {mods > 0 && <span className="ml-2"><Chip tone="amber">{mods} modifier{mods === 1 ? "" : "s"}</Chip></span>}
                              </td>
                              <td className="py-2 pr-3 text-right whitespace-nowrap">{money(t.price)}</td>
                              <td className="py-2 pr-3 text-gray-700">{t.tab?.name ?? <span className="text-gray-400">none</span>}</td>
                              <td className="py-2 pr-3 text-gray-600">{grants}</td>
                              <td className="py-2 pr-3 text-center">
                                <Toggle on={active} busy={togglingId === t.id} label={`${active ? "Deactivate" : "Activate"} ${t.name}`} onChange={() => toggleActive(row)} />
                              </td>
                              <td className="py-2 pr-3 text-center"><YesNo value={!!t.available_public_sale} /></td>
                              <td className="py-2 pr-3 whitespace-nowrap">
                                {errors ? <span className="text-red-700 font-medium">{errors} problem{errors === 1 ? "" : "s"}</span>
                                  : warnings ? <span className="text-amber-700 font-medium">{warnings} warning{warnings === 1 ? "" : "s"}</span>
                                  : <span className="text-green-700">Ready</span>}
                              </td>
                              <td className="py-2 text-right whitespace-nowrap">
                                <button type="button" className="text-primary-700 hover:text-primary-600 font-medium" onClick={() => navigate(ticketPath(t.id, "details"))}>Set up</button>
                                <Link to={`/ticketing-items/${t.id}/edit`} className="ml-3 text-gray-500">Full form</Link>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
            <div>
              <PrimaryButton onClick={() => navigate(ticketPath("new", "details"))}><PlusIcon className="h-4 w-4 mr-1" /> New ticket</PrimaryButton>
            </div>
          </Card>
        </>
      )}
    </div>
  );
};

/** Event-level settings, collapsible: storefront fields and the tab list. */
const EventSetupSection = ({ data, refresh, checks }: { data: SetupData; refresh: () => void; checks: SetupCheck[] }) => {
  const location = useLocation();
  const problems = checks.filter((c) => c.level !== "ok").length;
  const errors = checks.filter((c) => c.level === "error").length;
  const [open, setOpen] = useState<boolean>(errors > 0);
  useEffect(() => {
    if (location.hash === `#${EVENT_SETUP_ANCHOR}`) setOpen(true);
  }, [location.hash]);

  const event = data.event!;
  const proc = event.ticketing_payment_processor_config;
  const gateCount = data.gates.filter((g) => g.location_type === ACCESS_CONTROL_TYPE).length;
  const tabNames = data.tabs.filter((t) => t.is_active).sort((a, b) => a.sort_order - b.sort_order).map((t) => t.name);
  const summary = [
    event.ticketing_slug ? `ticketing.roninpos.app/events/${event.ticketing_slug}` : "no public URL",
    proc ? `${proc.processor ?? proc.payment_processor ?? "processor"} #${proc.id}` : "no processor",
    tabNames.length ? `tabs: ${tabNames.join(", ")}` : "no tabs",
    gateCount ? `${gateCount} gate${gateCount === 1 ? "" : "s"}` : "no gates",
  ].join(" · ");

  return (
    <div id={EVENT_SETUP_ANCHOR} className="bg-white rounded-md shadow-sm scroll-mt-4">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-3 px-5 py-3 text-left" aria-expanded={open}>
        <div className="flex items-center gap-2 min-w-0">
          {open ? <ChevronDownIcon className="h-5 w-5 text-gray-500 shrink-0" /> : <ChevronRightIcon className="h-5 w-5 text-gray-500 shrink-0" />}
          <span className="text-base font-semibold text-gray-900">Event Setup</span>
          <span className="text-sm text-gray-500 truncate">{summary}</span>
        </div>
        <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium ${errors ? "bg-red-100 text-red-800" : problems ? "bg-amber-100 text-amber-800" : "bg-green-100 text-green-800"}`}>
          {errors ? `${errors} problem${errors === 1 ? "" : "s"}` : problems ? `${problems} warning${problems === 1 ? "" : "s"}` : "ready"}
        </span>
      </button>
      {open && (
        <div className="px-5 pb-5 border-t border-gray-100 pt-4 flex flex-col gap-4">
          <StorefrontStep data={data} refresh={refresh} />
          <TabsManager data={data} refresh={refresh} />
          <GatesManager data={data} refresh={refresh} />
          <div className="border border-gray-200 rounded-md p-4">
            <div className="text-sm font-semibold text-gray-900 mb-1">Event checks</div>
            {checks.length === 0 ? <div className="text-sm text-gray-500">Nothing to report.</div> : checks.map((c, i) => <CheckRow key={i} check={c} />)}
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------- per-ticket flow

export interface StepProps {
  data: SetupData;
  refresh: () => void;
  /** null while creating a new ticket. */
  ticket: TicketingItem | null;
  ticketId: number | "new";
  checks: SetupCheck[];
}

export const TicketFlowPage = () => {
  const data = useLoaderData() as SetupData;
  const revalidator = useFollowHeaderEvent(data);
  const params = useParams();
  const refresh = () => revalidator.revalidate();
  const step: StepKey = isStepKey(params.step) ? params.step : "details";
  const ticketId: number | "new" = params.ticketId === "new" ? "new" : Number(params.ticketId);
  const ticket = ticketId === "new" ? null : data.tickets.find((t) => t.id === ticketId) ?? null;
  const checks = useMemo(() => (ticket ? computeTicketChecks(data, ticket) : []), [data, ticket]);
  const problems = useMemo(() => {
    const m: Partial<Record<StepKey, number>> = {};
    for (const c of checks) if (c.level !== "ok" && c.step && c.step !== "event") m[c.step] = (m[c.step] ?? 0) + 1;
    return m;
  }, [checks]);

  const stepProps: StepProps = { data, refresh, ticket, ticketId, checks };
  const title = ticket ? ticket.name : ticketId === "new" ? "New ticket" : "Ticket";

  return (
    <div className="mt-3 flex flex-col gap-4" aria-busy={revalidator.state === "loading"}>
      <PageHeader
        data={data}
        title={`Set up · ${title}`}
        sub={<><Link to={OVERVIEW_PATH}>All tickets</Link> · Five steps for this ticket type only. Every step reopens from the bar.</>}
      />
      {!data.eventId || !data.event ? (
        <NoEvent />
      ) : ticketId !== "new" && !ticket ? (
        <div className="bg-white rounded-md shadow-sm p-8 text-center text-sm text-gray-500">
          This ticket is not on the selected event. <Link to={OVERVIEW_PATH}>Back to tickets</Link>.
        </div>
      ) : (
        <>
          <Stepper current={step} ticketId={ticketId} problems={problems} />
          {step === "details" ? (
            <DetailsStep {...stepProps} />
          ) : step === "modifiers" ? (
            <ModifiersStep {...stepProps} />
          ) : step === "placement" ? (
            <PlacementStep {...stepProps} />
          ) : step === "gates" ? (
            <GatesStep {...stepProps} />
          ) : (
            <ReviewStep {...stepProps} />
          )}
        </>
      )}
    </div>
  );
};
